import { listChannels } from '@/features/channels/api';
import { listDepartments, listUsers } from '@/features/configuracoes/api';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCheck,
  ChevronDown,
  Clock,
  Loader2,
  type LucideIcon,
  PhoneIncoming,
  PhoneOutgoing,
  RefreshCw,
  Send,
  Timer,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { useRef, useState } from 'react';
import {
  type DashboardCharts,
  type DashboardFilters,
  type DashboardRatings,
  getCharts,
  getKpis,
  getRatings,
} from './api';

/** Paleta dos charts — espelha PALETTE original. */
const PALETTE = ['#0A4EE3', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#EF4444'];

/** Converte ISO datetime pra valor de <input type="datetime-local"> (yyyy-MM-ddTHH:mm). */
function toLocalDt(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Default: últimos 7 dias. */
function defaultRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function DashboardPage() {
  // Form state (datas em local datetime-local format pro input)
  const initial = defaultRange();
  const [dtStart, setDtStart] = useState(toLocalDt(initial.start));
  const [dtEnd, setDtEnd] = useState(toLocalDt(initial.end));
  const [userId, setUserId] = useState<number | null>(null);
  const [deptId, setDeptId] = useState<number | null>(null);
  const [channelId, setChannelId] = useState<number | null>(null);

  // Applied filters (só atualiza quando clica "Atualizar")
  const [applied, setApplied] = useState<DashboardFilters>(initial);

  const qc = useQueryClient();
  // Giro mínimo do botão Atualizar: a resposta pode voltar em ~100ms e o
  // clique parecia não ter funcionado. O spin segura ~650ms OU até o fetch
  // terminar (o que demorar mais).
  const [minSpin, setMinSpin] = useState(false);

  /**
   * Até quando os pedidos saem com `fresh` (a API calcula na hora em vez de
   * devolver o guardado de 60 s). Janela curta e não uma marca de uso único:
   * um clique dispara três pedidos em paralelo (indicadores, gráficos,
   * avaliações), e a marca seria gasta pelo primeiro. Passada a janela, as
   * recargas automáticas (voltar pra aba etc.) voltam a usar o cache.
   */
  const frescoAte = useRef(0);
  const opcoesDoPedido = () => ({ fresh: Date.now() < frescoAte.current });

  function applyFilters() {
    frescoAte.current = Date.now() + 3000;
    setApplied({
      start: new Date(dtStart).toISOString(),
      end: new Date(dtEnd).toISOString(),
      ...(userId !== null && { user_id: userId }),
      ...(deptId !== null && { department_id: deptId }),
      ...(channelId !== null && { whatsapp_number_id: channelId }),
    });
    // Sem isso, clicar com os filtros IGUAIS é no-op: a queryKey não muda e
    // o TanStack nem dispara request. Invalidate força o refetch sempre.
    void qc.invalidateQueries({ queryKey: ['dashboard-kpis'] });
    void qc.invalidateQueries({ queryKey: ['dashboard-charts'] });
    setMinSpin(true);
    window.setTimeout(() => setMinSpin(false), 650);
  }

  // keepPreviousData: mudar o filtro cria uma queryKey NOVA (sem cache) — sem
  // isso a tela limpava (spinners + charts sumindo) até a resposta chegar,
  // dando efeito de "flash". Com isso, os dados atuais ficam na tela e só são
  // substituídos quando os novos estão prontos.
  const kpisQ = useQuery({
    queryKey: ['dashboard-kpis', applied],
    queryFn: () => getKpis(applied, opcoesDoPedido()),
    placeholderData: keepPreviousData,
  });
  const ratingsQ = useQuery({
    queryKey: ['dashboard-ratings', applied],
    queryFn: () => getRatings(applied, opcoesDoPedido()),
    placeholderData: keepPreviousData,
  });
  const chartsQ = useQuery({
    queryKey: ['dashboard-charts', applied],
    queryFn: () => getCharts(applied, opcoesDoPedido()),
    placeholderData: keepPreviousData,
  });

  // Dropdowns de filtro (lazy — só carrega na montagem da página)
  const usersQ = useQuery({ queryKey: ['settings-users'], queryFn: () => listUsers() });
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: listChannels });

  return (
    <div className="flex-1 overflow-y-auto bg-n-50 p-6">
      <header className="mb-5">
        <h1 className="font-bold text-[20px] text-n-900">Dashboard</h1>
        <p className="text-[12px] text-n-500">Visão geral da operação.</p>
      </header>

      {/* Filter bar */}
      <div className="mb-5 grid grid-cols-1 gap-3 rounded-md border border-n-200 bg-n-0 p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-6">
        <FilterField label="Dt Início">
          <input
            type="datetime-local"
            value={dtStart}
            onChange={(e) => setDtStart(e.target.value)}
            className="h-[34px] rounded-md border border-n-200 bg-n-0 px-2.5 text-[12px] text-n-800 outline-none focus:border-brand-500"
          />
        </FilterField>
        <FilterField label="Dt Fim">
          <input
            type="datetime-local"
            value={dtEnd}
            onChange={(e) => setDtEnd(e.target.value)}
            className="h-[34px] rounded-md border border-n-200 bg-n-0 px-2.5 text-[12px] text-n-800 outline-none focus:border-brand-500"
          />
        </FilterField>
        <FilterField label="Usuário">
          <NativeSelect
            value={userId ?? ''}
            onChange={(v) => setUserId(v === '' ? null : Number(v))}
          >
            <option value="">Todos</option>
            {(usersQ.data ?? [])
              .filter((u) => u.status === 'active')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </NativeSelect>
        </FilterField>
        <FilterField label="Departamento">
          <NativeSelect
            value={deptId ?? ''}
            onChange={(v) => setDeptId(v === '' ? null : Number(v))}
          >
            <option value="">Todos</option>
            {(deptsQ.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </FilterField>
        <FilterField label="Canal">
          <NativeSelect
            value={channelId ?? ''}
            onChange={(v) => setChannelId(v === '' ? null : Number(v))}
          >
            <option value="">Todos</option>
            {(channelsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName ?? c.displayNumber}
              </option>
            ))}
          </NativeSelect>
        </FilterField>
        <div className="flex items-end">
          <button
            type="button"
            onClick={applyFilters}
            disabled={minSpin || kpisQ.isFetching || chartsQ.isFetching}
            className="flex h-[34px] w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-4 font-bold text-[12px] text-white transition-colors hover:bg-brand-600 active:scale-[0.98] disabled:opacity-60"
          >
            <RefreshCw
              size={13}
              className={
                minSpin || kpisQ.isFetching || chartsQ.isFetching ? 'animate-spin' : undefined
              }
            />
            Atualizar
          </button>
        </div>
      </div>

      {/* KPIs Row 1 (5) */}
      <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Kpi
          label="Total de atendimentos"
          value={fmtNumber(kpisQ.data?.tickets_created)}
          icon={CheckCheck}
          sub="no período"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Receptivos"
          value={fmtNumber(kpisQ.data?.receptive_count)}
          icon={PhoneIncoming}
          sub="criados por inbound"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Ativos"
          value={fmtNumber(kpisQ.data?.active_outbound_count)}
          icon={PhoneOutgoing}
          sub="criados por atendente"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Pendentes"
          value={fmtNumber(kpisQ.data?.tickets_pending_now)}
          icon={Clock}
          sub="aguardando agora"
          tone={(kpisQ.data?.tickets_pending_now ?? 0) > 10 ? 'warning' : 'default'}
          loading={kpisQ.isLoading}
        />
        {/* Atendente e bot. Sem campanha, Eventos de Mensagem, API externa, eco
            do celular, nota interna ou falha. Soma todos os canais; o filtro
            de canal mostra só o dele. */}
        <Kpi
          label="Mensagens enviadas"
          value={fmtNumber(kpisQ.data?.messages_sent)}
          icon={Send}
          sub="no período"
          loading={kpisQ.isLoading}
        />
      </div>

      {/* KPIs Row 2 (5) */}
      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Kpi
          label="Total contatos"
          value={fmtNumber(kpisQ.data?.contacts_total)}
          icon={Users}
          sub="no tenant"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Novos contatos"
          value={fmtNumber(kpisQ.data?.contacts_new)}
          icon={UserPlus}
          sub="no período"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Contatos ativos"
          value={fmtNumber(kpisQ.data?.contacts_active)}
          icon={UserCheck}
          sub="com inbound recente"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="TMA"
          value={formatDuration(kpisQ.data?.avg_handle_time_seconds ?? null)}
          icon={Clock}
          sub="tempo médio"
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="1ª resposta"
          value={formatDuration(kpisQ.data?.avg_first_response_seconds ?? null)}
          icon={Timer}
          sub="tempo médio"
          loading={kpisQ.isLoading}
        />
      </div>

      {/* 3 pies em uma linha */}
      {chartsQ.data && (
        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <PieCard title="Atendimentos por canal" data={chartsQ.data.by_channel} />
          {/* Motivo de fechamento usa horizontal bars (não pie). */}
          <ClosingReasonsCard data={chartsQ.data.by_closing_reason} />
          <PieCard title="Atendimentos por departamento" data={chartsQ.data.by_department} />
        </div>
      )}

      {/* Chart toggle: Por dia / Por hora */}
      {chartsQ.data && <TimeChartCard charts={chartsQ.data} />}

      {/* Pesquisa de Satisfação */}
      {ratingsQ.data && <RatingsCard data={ratingsQ.data} />}
    </div>
  );
}

/** Pesquisa de Satisfação — média, distribuição das notas e média por atendente. */
function RatingsCard({ data }: { data: DashboardRatings }) {
  const RATING_LABELS: Array<{ score: number; label: string }> = [
    { score: 5, label: 'Excelente 🤩' },
    { score: 4, label: 'Bom 😍' },
    { score: 3, label: 'Neutro 😐' },
    { score: 2, label: 'Ruim 🥺' },
    { score: 1, label: 'Péssimo 😔' },
  ];
  const max = Math.max(1, ...RATING_LABELS.map((r) => data.distribution[String(r.score)] ?? 0));
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* Média geral + distribuição */}
      <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm lg:col-span-2">
        <h3 className="mb-3 font-bold text-[13px] text-n-800">Pesquisa de Satisfação</h3>
        <div className="mb-4 flex items-baseline gap-3">
          <span className="font-bold text-[32px] text-n-900">
            {data.average != null ? data.average.toFixed(2).replace('.', ',') : '—'}
          </span>
          <span className="text-[13px] text-warning-600">
            {data.average != null ? '★'.repeat(Math.round(data.average)) : ''}
          </span>
          <span className="text-[12px] text-n-500">
            {data.total} avaliaç{data.total === 1 ? 'ão' : 'ões'} no período
          </span>
        </div>
        <div className="space-y-1.5">
          {RATING_LABELS.map((r) => {
            const c = data.distribution[String(r.score)] ?? 0;
            return (
              <div key={r.score} className="flex items-center gap-2">
                <span className="w-[110px] shrink-0 text-[12px] text-n-700">{r.label}</span>
                <div className="h-[10px] flex-1 overflow-hidden rounded-pill bg-n-100">
                  <div
                    className="h-full rounded-pill bg-brand-500"
                    style={{ width: `${(c / max) * 100}%` }}
                  />
                </div>
                <span className="w-[36px] shrink-0 text-right text-[12px] text-n-600">{c}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Média por atendente */}
      <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
        <h3 className="mb-3 font-bold text-[13px] text-n-800">Nota média por atendente</h3>
        {data.by_agent.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-n-500">Sem avaliações no período.</p>
        ) : (
          <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1">
            {data.by_agent.map((a) => (
              <div key={a.user_id ?? 'none'} className="flex items-center justify-between gap-2">
                <span className="truncate text-[12.5px] text-n-800">{a.name}</span>
                <span className="shrink-0 text-[12px] text-n-600">
                  <span className="font-semibold text-n-900">
                    {a.average.toFixed(2).replace('.', ',')}
                  </span>{' '}
                  <span className="text-warning-600">★</span>{' '}
                  <span className="text-n-400">({a.count})</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Componentes ----------

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-bold text-[10px] uppercase tracking-[0.05em] text-n-500">{label}</span>
      {children}
    </label>
  );
}

function NativeSelect({
  value,
  onChange,
  children,
}: {
  value: string | number;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-[34px] w-full appearance-none rounded-md border border-n-200 bg-n-0 pl-2.5 pr-8 text-[12px] text-n-800 outline-none focus:border-brand-500"
      >
        {children}
      </select>
      <ChevronDown
        size={13}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-n-400"
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  icon: Icon,
  sub,
  tone = 'default',
  loading = false,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  sub?: string;
  tone?: 'default' | 'warning';
  loading?: boolean;
}) {
  return (
    <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="font-bold text-[11px] uppercase tracking-[0.05em] text-n-500">
          {label}
        </span>
        <Icon
          size={14}
          strokeWidth={1.75}
          className={tone === 'warning' ? 'text-warning-600' : 'text-n-400'}
        />
      </div>
      <div
        className={`mt-1.5 font-bold text-[24px] tabular-nums leading-tight ${
          tone === 'warning' ? 'text-warning-700' : 'text-n-900'
        }`}
      >
        {loading ? <Loader2 size={18} className="animate-spin text-n-400" /> : value}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-n-500">{sub}</div>}
    </div>
  );
}

/** Pie chart (sem hole) com legenda absoluta top-right + tooltip nativo. */
function PieCard({
  title,
  data,
}: {
  title: string;
  data: Array<{ id: number; name: string; count: number }>;
}) {
  const total = data.reduce((acc, d) => acc + d.count, 0);
  return (
    <div className="relative rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
      <h3 className="font-semibold text-[13px] text-n-800">{title}</h3>
      {total === 0 ? (
        <EmptyChart />
      ) : (
        <>
          <div className="absolute right-[18px] top-[48px] flex max-w-[55%] flex-col gap-1.5 text-[12px]">
            {data.map((d, i) => (
              <div key={d.id} className="flex items-center gap-2 text-n-700">
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{ background: PALETTE[i % PALETTE.length] }}
                />
                <span className="truncate" title={d.name}>
                  {d.name}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center pt-2">
            <Pie data={data} total={total} />
          </div>
        </>
      )}
    </div>
  );
}

function Pie({
  data,
  total,
}: {
  data: Array<{ id: number; name: string; count: number }>;
  total: number;
}) {
  const cx = 100;
  const cy = 100;
  // Donut anel grosso (outer 80, inner 52). Sem total no centro.
  const rOuter = 80;
  const rInner = 52;

  // Tooltip estilizado seguindo o mouse — UX melhor que <title> nativo
  // (sem delay, sem cor do SO). Posição em coords do wrapper relative.
  const [hover, setHover] = useState<{
    name: string;
    count: number;
    pct: number;
    x: number;
    y: number;
    color: string;
  } | null>(null);

  function makeHandlers(name: string, count: number, pct: number, color: string) {
    return {
      onMouseMove: (e: React.MouseEvent<SVGElement>) => {
        const rect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
        if (!rect) return;
        setHover({
          name,
          count,
          pct,
          color,
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      },
      onMouseLeave: () => setHover(null),
    };
  }

  // 1 slice apenas → renderiza anel completo (path com 100% degenera em arco zero,
  // então usamos 2 circles concêntricos com mask via even-odd).
  const slices = (() => {
    if (data.length === 1 && data[0]) {
      const only = data[0];
      const color = PALETTE[0] ?? '#0A4EE3';
      return (
        <path
          d={`M ${cx - rOuter} ${cy} a ${rOuter} ${rOuter} 0 1 0 ${rOuter * 2} 0 a ${rOuter} ${rOuter} 0 1 0 -${rOuter * 2} 0 M ${cx - rInner} ${cy} a ${rInner} ${rInner} 0 1 1 ${rInner * 2} 0 a ${rInner} ${rInner} 0 1 1 -${rInner * 2} 0`}
          fill={color}
          fillRule="evenodd"
          {...makeHandlers(only.name, only.count, 100, color)}
          style={{ cursor: 'default' }}
        />
      );
    }
    let acc = 0;
    return data.map((d, i) => {
      const startAngle = (acc / total) * 2 * Math.PI;
      acc += d.count;
      const endAngle = (acc / total) * 2 * Math.PI;
      const large = endAngle - startAngle > Math.PI ? 1 : 0;
      // Outer arc points
      const x1Out = cx + rOuter * Math.sin(startAngle);
      const y1Out = cy - rOuter * Math.cos(startAngle);
      const x2Out = cx + rOuter * Math.sin(endAngle);
      const y2Out = cy - rOuter * Math.cos(endAngle);
      // Inner arc points (reverse direction)
      const x1In = cx + rInner * Math.sin(endAngle);
      const y1In = cy - rInner * Math.cos(endAngle);
      const x2In = cx + rInner * Math.sin(startAngle);
      const y2In = cy - rInner * Math.cos(startAngle);
      const pct = Math.round((d.count / total) * 100);
      const color = PALETTE[i % PALETTE.length] ?? '#0A4EE3';
      return (
        <path
          key={d.id}
          d={`M ${x1Out} ${y1Out} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2Out} ${y2Out} L ${x1In} ${y1In} A ${rInner} ${rInner} 0 ${large} 0 ${x2In} ${y2In} Z`}
          fill={color}
          {...makeHandlers(d.name, d.count, pct, color)}
          style={{ cursor: 'default' }}
        />
      );
    });
  })();

  return (
    <div className="relative">
      <svg viewBox="0 0 200 200" className="h-[190px] w-[190px]">
        {slices}
      </svg>
      {hover && (
        <div
          className="pointer-events-none absolute z-10 flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg"
          style={{
            left: hover.x + 12,
            top: hover.y + 12,
            background: 'rgba(15, 23, 42, 0.95)', // n-900 com 95% opacity
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: hover.color, boxShadow: '0 0 0 1px rgba(255,255,255,0.2)' }}
          />
          <span>
            <strong className="font-bold">{hover.name}</strong>
            <span className="mx-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              ·
            </span>
            <span className="tabular-nums">{hover.count}</span>
            <span className="mx-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              ·
            </span>
            <span className="tabular-nums">{hover.pct}%</span>
          </span>
        </div>
      )}
    </div>
  );
}

/** Bar chart horizontal pra motivo de fechamento (espelha BarsHorizontal de produção). */
function ClosingReasonsCard({
  data,
}: {
  data: Array<{ id: number; name: string; count: number }>;
}) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
      <h3 className="mb-3 font-semibold text-[13px] text-n-800">Motivo de fechamento</h3>
      {data.length === 0 ? (
        <EmptyChart />
      ) : (
        <div className="flex flex-col gap-3.5">
          {data.map((d, i) => (
            <div key={d.id} className="flex items-center gap-3 text-[12px]">
              <span className="w-[140px] flex-shrink-0 truncate text-n-700" title={d.name}>
                {d.name}
              </span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-pill bg-n-100">
                <div
                  className="h-full rounded-pill"
                  style={{
                    width: `${(d.count / max) * 100}%`,
                    background: PALETTE[i % PALETTE.length],
                  }}
                />
              </div>
              <span className="w-[40px] flex-shrink-0 text-right font-bold tabular-nums text-n-900">
                {d.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Card "Atendimentos no tempo" com toggle Por dia / Por hora. */
function TimeChartCard({ charts }: { charts: DashboardCharts }) {
  const [mode, setMode] = useState<'day' | 'hour'>('day');
  const data =
    mode === 'day'
      ? charts.by_day.map((d) => ({ label: formatDayLabel(d.day), value: d.count }))
      : charts.by_hour.map((h) => ({
          label: `${String(h.hour).padStart(2, '0')}h`,
          value: h.count,
        }));

  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
      <div className="mb-12 flex items-center justify-between">
        <h3 className="font-semibold text-[13px] text-n-800">Atendimentos no tempo</h3>
        <div className="inline-flex gap-1 rounded-md border border-n-200 bg-n-100 p-1">
          <button
            type="button"
            onClick={() => setMode('day')}
            className={`rounded-md px-3 py-1 font-semibold text-[12px] ${
              mode === 'day' ? 'bg-brand-500 text-white' : 'text-n-600 hover:bg-n-0'
            }`}
          >
            Por dia
          </button>
          <button
            type="button"
            onClick={() => setMode('hour')}
            className={`rounded-md px-3 py-1 font-semibold text-[12px] ${
              mode === 'hour' ? 'bg-brand-500 text-white' : 'text-n-600 hover:bg-n-0'
            }`}
          >
            Por hora
          </button>
        </div>
      </div>
      <BarChart data={data} max={max} />
    </div>
  );
}

function BarChart({
  data,
  max,
}: {
  data: Array<{ label: string; value: number }>;
  max: number;
}) {
  if (data.length === 0) return <EmptyChart />;
  // Altura total reservada pra área das barras (em px). Label abaixo soma ~20px.
  const chartHeight = 260;
  return (
    <div className="flex flex-col" style={{ height: chartHeight + 32 }}>
      {/* Área das barras com baseline sólida sutil */}
      <div
        className="relative flex gap-1 px-1"
        style={{ height: chartHeight, borderBottom: '1px solid #E2E8F0' }}
      >
        {data.map((d, i) => {
          const barH = max > 0 ? Math.max(2, Math.round((d.value / max) * chartHeight)) : 2;
          return (
            <div
              key={`${d.label}-${i}-bar`}
              className="flex flex-1 flex-col items-center justify-end min-w-0"
            >
              <div
                className="w-full max-w-[64px] rounded-t-md bg-brand-500 flex items-start justify-center"
                style={{ height: `${barH}px` }}
                title={`${d.label}: ${d.value}`}
              >
                {d.value > 0 && barH > 18 && (
                  <span className="pt-1 font-bold text-[11px] tabular-nums text-white">
                    {d.value}
                  </span>
                )}
                {d.value > 0 && barH <= 18 && (
                  <span className="-mt-4 font-bold text-[11px] tabular-nums text-n-700">
                    {d.value}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* X-axis labels alinhados com as barras */}
      <div className="flex gap-1 px-1 pt-1.5">
        {data.map((d, i) => (
          <span
            key={`${d.label}-${i}-label`}
            className="flex-1 text-center font-medium text-[11px] text-n-500 tabular-nums min-w-0"
          >
            {d.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-[140px] items-center justify-center text-[12px] text-n-500">
      Sem dados no período.
    </div>
  );
}

// ---------- Formatters ----------

function fmtNumber(n: number | undefined): string {
  if (n === undefined) return '—';
  return n.toLocaleString('pt-BR');
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/** "2026-05-11" → "11/05" */
function formatDayLabel(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}
